/*
 *  -----------------------------------------------------------------------
 *    <copyright file="reports-templates-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { OpraReportTemplate } from '@interfaces/report-templates.interface';
import React, { useEffect } from 'react';
import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import { ExcelExportConfig } from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { TEMPLATE_GRID_COLUMNS } from '@components/shared/manage-reports-templates/reports-templates-grid/reports-templates-grid.data';
import { TemplatesPaneProps } from '@components/shared/manage-reports-templates/reports-templates-grid/reports-templates-grid.interface';
import { useLocalState, useTranslation } from '@hooks/index';
import Constants, { ModuleNames } from '@utils/constants';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

import './styles.scss';

const ReportTemplatesGrid = ({ module, currentTemplate, setCurrentTemplate, gridData }: TemplatesPaneProps) => {
    const { t } = useTranslation();
    const localState = useLocalState();

    const studyName = localState.state?.selectedStudy?.studyNumber;
    const moduleName = module === Constants.Modules.CM ? ModuleNames.CM : ModuleNames.RAM;
    const exportFileName = `${studyName}_Study ${moduleName} Report Templates List_${stringDateWithoutTimeFromUtc(
        new Date(),
        'DD-MMM-YYYY',
    )}`;

    const exportConfig: ExcelExportConfig = {
        fileName: exportFileName,
        useColumn: true,
        useData: true,
    };

    useEffect(() => {
        if (!gridData?.length) return;
        setCurrentTemplate(gridData[0]);
    }, [gridData]);

    const onFilteredItemsChange = (filteredItems: OpraReportTemplate[]) => {
        if (currentTemplate && filteredItems.findIndex((item) => item === currentTemplate.id) === -1) {
            setCurrentTemplate(filteredItems[0]);
        }
    };

    return (
        <ExpandableGridV2
            title={t('templates')}
            customKey="reports-templates-grid"
            id="reports-templates-grid"
            className="reports-templates-height"
            items={gridData}
            groupable
            reorderable
            scrollable="scrollable"
            resizable
            onFilteredItemsChange={onFilteredItemsChange}
            pageable={false}
            columns={TEMPLATE_GRID_COLUMNS(t, module)}
            hideCheckbox
            showFirstColumn={false}
            hideExpand
            selectable={{
                enabled: true,
                mode: 'single',
            }}
            fixedScroll
            defaultSelected={currentTemplate}
            setCurrentItem={setCurrentTemplate}
            exportConfig={exportConfig}
        />
    );
};

export default ReportTemplatesGrid;
